import React, { useState } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Descriptions, Table, Divider,
  Drawer, Form, Input, Modal, Space, message, Popconfirm, Alert, Select, Tooltip, Switch
} from 'antd';
import {
  EditOutlined, EyeOutlined, ReloadOutlined,
  KeyOutlined, SearchOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { TextArea } = Input;

const STATUS_MAP = {
  pending: { label: '待审核', color: 'processing' },
  active: { label: '已生效', color: 'green' },
  rejected: { label: '已驳回', color: 'red' },
  disabled: { label: '已停用', color: 'default' },
};

const MOCK_SAAS = {
  status: 'active',
  appName: '智能家居控制平台',
  saasId: 'SAAS_ENT001_A3F8',
  description: '企业自建的智能家居设备管理SaaS系统',
  callbackTest: 'https://api.example.com/haomeng/callback',
  accessKey: 'AK_SAAS_ENT001_A3F8',
  secretKey: 'SK_xxxxa1b2',
  registerTime: '2026-05-28 14:30:00',
  callbackEnabled: true,
};

const MOCK_PRODUCTS = [
  { key: '1', enterpriseName: '深圳智联科技有限公司', enterpriseId: 'ENT_001', name: '智能摄像机 Q1', productId: 'PROD_001', authStatus: '已授权', authTime: '2026-05-28 15:00:00' },
  { key: '2', enterpriseName: '杭州云智设备有限公司', enterpriseId: 'ENT_008', name: '智能门锁 D1', productId: 'PROD_002', authStatus: '待确认', authTime: '2026-05-29 10:00:00' },
  { key: '3', enterpriseName: '北京万物互联科技有限公司', enterpriseId: 'ENT_003', name: '4G室外球机', productId: 'PROD_003', authStatus: '待确认', authTime: '2026-05-30 08:00:00' },
  { key: '4', enterpriseName: '星云互联有限公司', enterpriseId: 'ENT_005', name: '红外枪机', productId: 'PROD_004', authStatus: '我已拒绝', authTime: '2026-05-29 14:00:00' },
  { key: '5', enterpriseName: '深圳智联科技有限公司', enterpriseId: 'ENT_001', name: '全景摄像机 P360', productId: 'PROD_005', authStatus: '已取消授权', authTime: '2026-05-28 09:00:00' },
];

// TODO: 联调时替换 — PaaS 提供的订阅 topic 列表
const MOCK_TOPICS = [
  { key: 'device_online', name: '设备上线通知', desc: '设备连接PaaS时触发' },
  { key: 'device_offline', name: '设备离线通知', desc: '设备断开PaaS时触发' },
  { key: 'alarm_event', name: '报警事件', desc: '设备触发报警时通知' },
  { key: 'ota_result', name: 'OTA升级结果', desc: 'OTA升级完成时通知结果' },
  { key: 'data_report', name: '数据上报', desc: '设备周期上报数据时触发' },
  { key: 'device_bind', name: '设备绑定', desc: '新设备绑定时触发' },
  { key: 'device_unbind', name: '设备解绑', desc: '设备解除绑定时触发' },
  { key: 'firmware_upgrade', name: '固件升级', desc: '设备固件升级时通知' },
  { key: 'cloud_record', name: '云端录像', desc: '云端录像完成时触发' },
  { key: 'face_detect', name: '人脸检测', desc: 'AI人脸检测到目标时触发' },
];

const MOCK_UNREGISTERED = false;

const SelfSaaS = () => {
  const [saas, setSaas] = useState(MOCK_UNREGISTERED ? null : MOCK_SAAS);
  const [registered, setRegistered] = useState(!MOCK_UNREGISTERED);
  const [products, setProducts] = useState(MOCK_PRODUCTS);

  const [registerVisible, setRegisterVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);

  // IP白名单
  const [ipList, setIpList] = useState(['192.168.1.100', '10.0.0.1']);
  const [ipInput, setIpInput] = useState('');
  // 回调重试次数
  const [callbackEnabled, setCallbackEnabled] = useState(saas?.callbackEnabled ?? true);
  // 订阅 topic
  const [topics, setTopics] = useState(MOCK_TOPICS.map(t => ({ ...t, subscribed: true })));

  const [skVisible, setSkVisible] = useState(false);
  const [resetSkVisible, setResetSkVisible] = useState(false);
  const [skFailCount, setSkFailCount] = useState(0);
  const [skLocked, setSkLocked] = useState(false);
  const [fullSk, setFullSk] = useState('');

  const [registerForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pwdForm] = Form.useForm();

  // 注册
  const handleRegister = () => {
    registerForm.validateFields().then(values => {
      setSaas({ status: 'pending', appName: values.appName, saasId: 'SAAS_ENT001_' + Math.random().toString(36).substr(2, 4).toUpperCase(), description: values.description || '', callbackTest: '', accessKey: '', secretKey: '', registerTime: new Date().toLocaleString('zh-CN', { hour12: false }) });
      setRegistered(true); setRegisterVisible(false);
      message.success('SaaS 已提交审核');
    });
  };

  // 编辑
  const handleEdit = () => { editForm.setFieldsValue({ appName: saas.appName, description: saas.description, callbackTest: saas.callbackTest, callbackEnabled: saas.callbackEnabled }); setEditVisible(true); };
  const handleSaveEdit = () => { editForm.validateFields().then(values => { setSaas({ ...saas, ...values }); setCallbackEnabled(values.callbackEnabled); setEditVisible(false); message.success('保存成功'); }); };

  // 查看SK
  const handleViewSk = () => { if (skLocked) { message.error('密码错误次数过多，请 30 分钟后重试'); return; } pwdForm.resetFields(); setSkVisible(true); };
  const handleSkPwdConfirm = () => {
    pwdForm.validateFields().then(values => {
      if (values.password === 'test') { const sk = 'SK_' + saas.saasId + '_' + Math.random().toString(36).substr(2, 8); setFullSk(sk); setSkFailCount(0); message.success('验证通过'); }
      else { const c = skFailCount + 1; setSkFailCount(c); if (c >= 3) { setSkLocked(true); setSkVisible(false); message.error('密码错误次数过多，请 30 分钟后重试'); setTimeout(() => setSkLocked(false), 30 * 60 * 1000); } else message.error(`密码错误，还剩 ${3 - c} 次机会`); }
    });
  };

  // 重置SK
  const handleResetSk = () => { pwdForm.resetFields(); setResetSkVisible(true); };
  const handleResetSkConfirm = () => {
    pwdForm.validateFields().then(values => {
      if (values.password === 'test') { setSaas({ ...saas, secretKey: 'SK_xxxx' + Math.random().toString(36).substr(2, 4) }); message.success('SK 已重置'); setResetSkVisible(false); }
      else message.error('密码错误');
    });
  };

  // 授权产品
  const handleAgree = (record) => { setProducts(products.map(p => p.key === record.key ? { ...p, authStatus: '已授权' } : p)); message.success('已同意授权'); };
  const handleReject = (record) => { setProducts(products.map(p => p.key === record.key ? { ...p, authStatus: '我已拒绝' } : p)); message.success('已拒绝'); };
  const handleCancelAuth = (record) => { setProducts(products.map(p => p.key === record.key ? { ...p, authStatus: '已取消授权' } : p)); message.success('已取消授权'); };

  // --- 未注册 ---
  if (!registered) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>SaaS应用</Title>
        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', textAlign: 'center', padding: '60px 0' }}>
          <KeyOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
          <div style={{ marginTop: 16, fontSize: 16, color: '#595959' }}>您尚未注册SaaS应用</div>
          <div style={{ marginTop: 8, color: '#999', fontSize: 13 }}>注册后可获取SaaS ID和AK/SK，用于对接PaaS</div>
          <Button type="primary" size="large" style={{ marginTop: 24 }} onClick={() => { registerForm.resetFields(); setRegisterVisible(true); }}>注册SaaS</Button>
        </Card>
        <Drawer title="注册SaaS" open={registerVisible} onClose={() => setRegisterVisible(false)} width={480} destroyOnClose
          footer={<div style={{ textAlign: 'right' }}><Button onClick={() => setRegisterVisible(false)}>取消</Button><Button type="primary" style={{ marginLeft: 8 }} onClick={handleRegister}>提交</Button></div>}>
          <Form form={registerForm} layout="vertical">
            <Form.Item name="appName" label="应用名称" rules={[{ required: true, message: '请输入应用名称' }]}><Input placeholder="请输入SaaS应用名称" /></Form.Item>
            <Form.Item name="description" label="应用描述"><TextArea rows={3} placeholder="SaaS应用描述（选填）" /></Form.Item>
          </Form>
        </Drawer>
      </div>
    );
  }

  const statusCfg = STATUS_MAP[saas.status];
  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>SaaS应用</Title>
      {saas.status === 'pending' && <Alert type="info" showIcon message="SaaS 审核中，通过后方可使用 AK/SK 和产品授权。" style={{ marginBottom: 16, borderRadius: 8 }} />}
      {saas.status === 'rejected' && <Alert type="warning" showIcon message="SaaS 审核被驳回，请修改信息后重新提交审核。" style={{ marginBottom: 16, borderRadius: 8 }} />}
      <Row gutter={16}>
        <Col span={12}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
            title={<Space><span>基本信息</span><Tag color={statusCfg.color}>{statusCfg.label}</Tag></Space>}
            extra={<Button type="link" icon={<EditOutlined />} onClick={handleEdit}>编辑</Button>}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="应用名称">{saas.appName}</Descriptions.Item>
              <Descriptions.Item label="SaaS ID"><Text copyable style={{ fontFamily: 'monospace' }}>{saas.saasId}</Text></Descriptions.Item>
              <Descriptions.Item label="应用描述">{saas.description || '—'}</Descriptions.Item>
              <Descriptions.Item label="开启回调">
                <Tag color={callbackEnabled ? 'green' : 'default'}>{callbackEnabled ? '开启' : '关闭'}</Tag>
              </Descriptions.Item>
              {callbackEnabled && (
                <>
                  <Descriptions.Item label="回调地址">{saas.callbackTest || '—'}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="注册时间">{saas.registerTime}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          {saas.status === 'active' ? (
            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }} title="AK/SK">
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Access Key ID"><Text copyable style={{ fontFamily: 'monospace' }}>{saas.accessKey}</Text></Descriptions.Item>
                <Descriptions.Item label="Secret Access Key">
                  <Space>
                    <Text style={{ fontFamily: 'monospace' }}>{'********' + (saas.secretKey || '').slice(-4)}</Text>
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={handleViewSk}>查看SK</Button>
                    <Button type="link" size="small" danger icon={<ReloadOutlined />} onClick={handleResetSk}>重置SK</Button>
                  </Space>
                </Descriptions.Item>
              </Descriptions>
              <Divider style={{ margin: '12px 0' }} />
              <div style={{ fontWeight: 500, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span>IP 白名单</span><Text type="secondary" style={{ fontSize: 12 }}>{ipList.length}/20</Text>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Input placeholder="输入 IP 地址" size="small" value={ipInput} onChange={e => setIpInput(e.target.value)} style={{ flex: 1 }} />
                <Button size="small" onClick={() => { if (!ipInput) return; if (ipList.length >= 20) { message.warning('最多 20 条'); return; } if (ipList.includes(ipInput)) { message.warning('IP 已存在'); return; } setIpList([...ipList, ipInput]); setIpInput(''); }}>添加</Button>
              </div>
              {ipList.length === 0 ? <Text type="secondary" style={{ fontSize: 12 }}>暂无白名单，所有 IP 均可访问</Text> : (
                <Space wrap>{ipList.map(ip => (<Tag closable key={ip} onClose={() => setIpList(ipList.filter(i => i !== ip))}>{ip}</Tag>))}</Space>
              )}
            </Card>
          ) : (
            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>审核通过后将展示 AK/SK</div>
            </Card>
          )}
        </Col>
      </Row>

      {/* 订阅 topic */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginTop: 16 }} title="订阅 Topic"
        extra={saas.status === 'active' && callbackEnabled
          ? <Button type="primary" onClick={() => message.success('已保存')}>保存</Button>
          : <Tooltip title={saas.status !== 'active' ? 'SaaS 审核通过后方可配置' : '请先开启回调'}><Button type="primary" disabled>保存</Button></Tooltip>}>
        <Table size="small" rowKey="key" dataSource={topics} pagination={false} bordered={false} scroll={{ y: 200 }}
          columns={[
            { title: 'Topic', dataIndex: 'key', key: 'key', width: 140, render: v => <Text code>{v}</Text> },
            { title: '名称', dataIndex: 'name', key: 'name', width: 140 },
            { title: '描述', dataIndex: 'desc', key: 'desc' },
            {
              title: '订阅', key: 'subscribed', width: 80,
              render: (_, r) => <Switch size="small" disabled={saas.status !== 'active' || !callbackEnabled} checked={r.subscribed} onChange={v => setTopics(topics.map(t => t.key === r.key ? { ...t, subscribed: v } : t))} />
            },
          ]} />
        {!callbackEnabled && saas.status === 'active' && <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>开启回调后可配置订阅 Topic</div>}
      </Card>

      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginTop: 16 }} title="产品授权申请">
        <Alert message="您的所有产品均默认授权此SaaS应用，无需配置。同时您可授权其他企业的产品使用您的SaaS应用。" type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }} />
        {saas.status !== 'active' ? <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>审核通过后查看授权申请</div> : (
          <>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Space.Compact>
                <Select defaultValue="name" style={{ width: 130 }}><Select.Option value="name">申请企业</Select.Option><Select.Option value="id">申请企业ID</Select.Option></Select>
                <Input placeholder="请输入" allowClear style={{ width: 150 }} />
              </Space.Compact>
              <Space.Compact>
                <Select defaultValue="name" style={{ width: 130 }}><Select.Option value="name">授权产品</Select.Option><Select.Option value="id">产品ID</Select.Option></Select>
                <Input placeholder="请输入" allowClear style={{ width: 150 }} />
              </Space.Compact>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />}>重置</Button>
            </div>
            <Table size="small" rowKey="key" dataSource={products} bordered={false}
              pagination={{ showSizeChanger: true, showTotal: t => `共 ${t} 条记录`, pageSize: 10 }}
            columns={[
              { title: '申请企业', key: 'enterprise', width: 200, render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.enterpriseName}</div><Text type="secondary" style={{ fontSize: 12 }}>ID: {r.enterpriseId}</Text></div>) },
              { title: '授权产品', key: 'product', render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.name}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text></div>) },
              { title: '授权状态', dataIndex: 'authStatus', key: 'authStatus', width: 100, render: v => {
                const m = { '已授权': 'green', '待确认': 'processing', '已拒绝': 'red', '已撤销': 'default' };
                return <Tag color={m[v] || 'default'} style={{ border: 'none' }}>{v}</Tag>;
              } },
              { title: '申请时间', dataIndex: 'authTime', key: 'authTime' },
              { title: '操作', key: 'action', render: (_, r) => {
                if (r.authStatus === '待确认') return (
                  <Space>
                    <Button type="link" size="small" onClick={() => handleAgree(r)}>同意授权</Button>
                    <Popconfirm title="确认拒绝此授权申请？" onConfirm={() => handleReject(r)} okText="确认" cancelText="取消">
                      <Button type="link" size="small" danger>拒绝授权</Button>
                    </Popconfirm>
                  </Space>
                );
                if (r.authStatus === '已授权') return (
                  <Popconfirm title="确认取消此授权？取消后该产品将无法访问 SaaS" onConfirm={() => handleCancelAuth(r)} okText="确认" cancelText="取消">
                    <Button type="link" size="small" danger>取消授权</Button>
                  </Popconfirm>
                );
                return <Text type="secondary">—</Text>;
              } },
            ]}
            locale={{ emptyText: '暂无合作伙伴授权产品' }} />
          </>
        )}
      </Card>

      {/* 编辑抽屉 */}
      <Drawer title="编辑SaaS信息" open={editVisible} onClose={() => setEditVisible(false)} width={480} destroyOnClose
        footer={<div style={{ textAlign: 'right' }}><Button onClick={() => setEditVisible(false)}>取消</Button><Button type="primary" style={{ marginLeft: 8 }} onClick={handleSaveEdit}>保存</Button></div>}>
        <Form form={editForm} layout="vertical">
          <Form.Item name="appName" label="应用名称" rules={[{ required: true, message: '请输入应用名称' }]}><Input /></Form.Item>
          <Form.Item name="description" label="应用描述"><TextArea rows={3} /></Form.Item>
          <Form.Item name="callbackEnabled" label="开启回调" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.callbackEnabled !== cur.callbackEnabled}>
            {({ getFieldValue }) => getFieldValue('callbackEnabled') ? (
              <>
                <Form.Item name="callbackTest" label={<span>回调地址 <Text type="secondary" style={{ fontSize: 12 }}>如 https://api.example.com/haomeng/callback</Text></span>} rules={[{ required: true, message: '请输入回调地址' }]}><Input placeholder="回调地址" /></Form.Item>
              </>
            ) : null}
          </Form.Item>
        </Form>
      </Drawer>

      {/* SK 弹窗 */}
      <Modal title="身份验证" open={skVisible} onCancel={() => setSkVisible(false)} onOk={handleSkPwdConfirm} okText="确认" cancelText="取消" destroyOnClose>
        {fullSk ? (
          <div><div style={{ marginBottom: 8, color: '#595959' }}>Secret Access Key（复制后请妥善保管）：</div>
            <Input.TextArea value={fullSk} rows={2} readOnly style={{ fontFamily: 'monospace' }} />
            <Button type="link" size="small" style={{ marginTop: 8 }} onClick={() => { try { navigator.clipboard?.writeText(fullSk); } catch {} message.success('已复制'); }}>复制 SK</Button></div>
        ) : (
          <Form form={pwdForm} layout="vertical"><div style={{ marginBottom: 12, color: '#595959' }}>请输入当前账号密码以查看 Secret Access Key</div>
            <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}><Input.Password placeholder="请输入密码" /></Form.Item></Form>)}
      </Modal>

      {/* SK重置弹窗 */}
      <Modal title="重置 Secret Access Key" open={resetSkVisible} onCancel={() => setResetSkVisible(false)} onOk={handleResetSkConfirm} okText="确认重置" cancelText="取消" okButtonProps={{ danger: true }} destroyOnClose>
        <div style={{ marginBottom: 16 }}><Text type="danger" style={{ fontSize: 14 }}>重置后旧 SK 即时失效，所有使用旧 SK 的请求将被拒绝。确认重置吗？</Text></div>
        <Form form={pwdForm} layout="vertical"><Form.Item name="password" label="请输入当前账号密码" rules={[{ required: true, message: '请输入密码' }]}><Input.Password placeholder="请输入密码" /></Form.Item></Form>
      </Modal>
    </div>
  );
};

export default SelfSaaS;
